// src/pages/Swap/hooks/useSwapExecution.js - Updated with proper internationalization
/* global BigInt */

import { useState, useContext } from 'react';
import { ethers, formatUnits, parseUnits } from 'ethers';
import { useTranslation } from 'react-i18next';
import { 
  getBitDogSwapContract, 
  isNativeToken, 
  getActualTokenDecimals,
  formatAmountForDisplay 
} from '../utils/SwapUtils';
import { isUsingSushiSwap } from '../../../services/sushiswapApi';
import { TransactionContext, TRANSACTION_TYPES } from '../../../contexts/TransactionContext';
import { NetworkContext } from '../../../contexts/NetworkContext';
import { formatGasPriceForDisplay, formatGasPriceForTransaction } from '../../Wallet/utils/gasUtils';
import { 
  logTransactionDetails, 
  analyzeTransactionError,
  analyzeSushiSwapError
} from '../../../utils/transactionDiagnostics';
import { ETH_ADDRESS } from '../utils/SwapConstants';


/**
 * Hook for handling swap execution logic
 */
export const useSwapExecution = (
  provider, 
  currentWallet, 
  currentChain,
  notificationRef, 
  autoConfirm
) => {
  const { t } = useTranslation();
  
  // Extract needed values from currentChain
  const aggregatorAddress = currentChain?.aggregatorAddress;
  const explorerUrl = currentChain?.explorerUrl;
  
  const [isSwapping, setIsSwapping] = useState(false);
  const [txnHash, setTxnHash] = useState(null);
  const [swapError, setSwapError] = useState(null);
  const [tempGasOption, setTempGasOption] = useState(null);
  const [tempGasPrice, setTempGasPrice] = useState(null);
  const [confirmationDetails, setConfirmationDetails] = useState(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  
  // Get transaction context functions
  const { addTransaction, updateTransactionStatus } = useContext(TransactionContext);
  // Get the current chain to correctly set network
  const { currentChain: contextChain } = useContext(NetworkContext);

  /**
   * Safely validate and parse amount
   * @param {string} amount - Amount to validate
   * @returns {boolean} - True if amount is valid
   */
  const isValidAmount = (amount) => {
    if (!amount) return false;
    const num = parseFloat(amount);
    return !isNaN(num) && num > 0;
  };

  /**
   * Improve error message readability for user display
   * @param {string} errorMessage - Raw error message 
   * @returns {string} - User-friendly error message
   */
  const getReadableErrorMessage = (errorMessage) => {
    // Don't show SushiSwap specific errors to users
    const userFriendlyErrors = {
      "insufficient allowance": t('swap.errors.insufficientAllowance'),
      "Insufficient token allowance": t('swap.errors.insufficientAllowance'),
      "INSUFFICIENT_OUTPUT_AMOUNT": t('swap.errors.priceMoved'),
      "Too little received": t('swap.errors.priceMoved'),
      "EXCESSIVE_INPUT_AMOUNT": t('swap.errors.priceMovedFavorably'),
      "insufficient funds": t('swap.errors.insufficientFunds'),
      "ETH_BALANCE_TOO_LOW": t('swap.errors.insufficientFundsForGas'),
      "TRANSFER_FROM_FAILED": t('swap.errors.transferFailed'),
      "price impact too high": t('swap.errors.priceImpactTooHigh'),
      "SushiSwap API error": t('swap.errors.serviceUnavailable'),
      "Failed to fetch": t('swap.errors.networkError'),
      "User rejected": t('swap.errors.userRejected'),
      "user denied": t('swap.errors.userRejected'),
      "Transaction discarded": t('swap.errors.networkRejected'),
      "execution reverted": t('swap.errors.transactionReverted'),
      "gas required exceeds allowance": t('swap.errors.gasRequired')
    };

    // Look for known error patterns
    for (const [errorPattern, friendlyMessage] of Object.entries(userFriendlyErrors)) {
      if (errorMessage.toLowerCase().includes(errorPattern.toLowerCase())) {
        return friendlyMessage;
      }
    }

    // If no specific match, remove SushiSwap references and keep it concise
    let cleanedMessage = errorMessage
      .replace(/SushiSwap/g, "Exchange")
      .replace(/API/g, "service");

    // If message is too long, keep only first sentence or 80 chars
    if (cleanedMessage.length > 80) {
      const firstSentence = cleanedMessage.split('.')[0];
      if (firstSentence && firstSentence.length < 80) {
        return firstSentence + '.';
      }
      return cleanedMessage.substring(0, 80) + '...';
    }

    return cleanedMessage;
  };

/**
 * Execute swap using SushiSwap API transaction data
 */
const executeSushiSwap = async (
  inputToken,
  outputToken,
  amountIn,
  amountOutMin,
  slippage,
  gasOption,
  customGasPrice,
  dynamicGasPrice,
  userAddress,
  deadline,
  routeSummary,
  txOptions = {}
) => {
  // Validate input parameters
  if (!isValidAmount(amountIn)) {
    throw new Error(t('swap.errors.invalidInputAmount'));
  }
  if (!isValidAmount(amountOutMin)) {
    throw new Error(t('swap.errors.invalidMinimumOutputAmount'));
  }
  if (!routeSummary || !routeSummary._tx) {
    throw new Error(t('swap.errors.missingTransactionData'));
  }
  if (!userAddress) {
    throw new Error(t('swap.errors.userAddressRequired'));
  }

  // Get decimals for input token (to format the value correctly)
  const inputDecimals = await getActualTokenDecimals(provider, inputToken);
  
  // CRITICAL: Validate the transaction value against input amount
  if (isNativeToken(inputToken.address) && routeSummary._tx.value) {
    try {
      // Parse the input amount with correct decimals
      const amountInWei = parseUnits(amountIn.toString(), inputDecimals).toString();
      const txValue = routeSummary._tx.value.toString();
      
      // If the values don't match, log a warning
      if (amountInWei !== txValue) {
        console.warn(`Transaction value mismatch: input=${amountInWei}, tx.value=${txValue}`);
        console.warn("This may indicate using an outdated quote. Consider refreshing.");
        
        // If difference is too large (>1%), abort or warn user
        const amountBigInt = BigInt(amountInWei);
        const txValueBigInt = BigInt(txValue);
        
        // Calculate relative difference
        const diff = amountBigInt > txValueBigInt ? 
          (amountBigInt - txValueBigInt) : (txValueBigInt - amountBigInt);
        
        // Convert to percentage
        const relDiff = Number(diff * BigInt(10000) / amountBigInt) / 100;
        
        if (relDiff > 1.0) {
          console.error(`Amount mismatch too large (${relDiff.toFixed(2)}%)! Aborting swap for safety.`);
          throw new Error(t('swap.errors.priceMoved') + ` (${relDiff.toFixed(2)}%)`);
        } else {
          console.warn(`Amount mismatch within tolerance (${relDiff.toFixed(2)}%). Proceeding with caution.`);
        }
      }
    } catch (error) {
      // If the error is from our validation, rethrow it
      if (error.message.includes("Transaction amount mismatch")) {
        throw error;
      }
      
      // Otherwise, log but continue (don't block the transaction)
      console.error("Error validating transaction amount:", error);
    }
  }
  
  // Get transaction parameters from helper function
  const { getSushiSwapTransactionParams } = await import('../../../services/sushiswapApi');
  
  console.log("Swap execution parameters:", {
    transactionTo: routeSummary._tx.to,
    inputToken: inputToken.symbol,
    outputToken: outputToken.symbol,
    amountIn,
    amountOutMin,
    isNativeToken: isNativeToken(inputToken.address),
    userAddress,
    gasOption,
    customGasPrice
  });
  
  try {
    // Create wallet with signer
    const wallet = new ethers.Wallet(currentWallet.privateKey, provider);
    const walletAddress = wallet.address.toLowerCase();
    
    console.log(`Using wallet address for swap: ${walletAddress}`);
    
    // Get transaction parameters for SushiSwap
    const txParams = getSushiSwapTransactionParams(routeSummary, txOptions);
    
    // Add gas settings from user preferences if available
    if (gasOption === 'custom' && customGasPrice) {
      try {
        // Format the gas price to ensure consistent precision
        const formattedGasPrice = formatGasPriceForTransaction(customGasPrice);
        // Parse gas price with proper units
        const parsedGasPrice = parseUnits(formattedGasPrice, 'gwei');
        txOptions.gasPrice = parsedGasPrice;
        console.log(`Using custom gas price from parameters: ${formattedGasPrice} Gwei`);
      } catch (error) {
        console.error("Error parsing custom gas price:", error);
      }
    } else if (dynamicGasPrice) {
      try {
        const basePrice = parseFloat(dynamicGasPrice);
        let multiplier = 1.0; // default medium
        
        if (gasOption === 'slow') multiplier = 0.8;
        else if (gasOption === 'fast') multiplier = 1.2;
        
        const calculatedPrice = (basePrice * multiplier);
        // Format the calculated price to ensure consistent precision
        const formattedGasPrice = formatGasPriceForTransaction(calculatedPrice);
        txOptions.gasPrice = parseUnits(formattedGasPrice, 'gwei');
        console.log(`Using ${gasOption} gas price: ${formattedGasPrice} Gwei (base: ${basePrice}, multiplier: ${multiplier})`);
      } catch (error) {
        console.error("Error setting dynamic gas price:", error);
      }
    }
    
    // Execute the transaction
    console.log("Sending transaction:", {
      to: txParams.to,
      value: txParams.value ? txParams.value.toString() : '0',
      gasLimit: txParams.gasLimit.toString(),
      dataLength: txParams.data.length,
      dataPrefix: txParams.data.substring(0, 10),
      gasPrice: txParams.gasPrice ? formatUnits(txParams.gasPrice, 'gwei') : 'default'
    });
    
    // Use the new diagnostics utility to log detailed transaction info
    if (typeof logTransactionDetails === 'function') {
      logTransactionDetails(txParams, 'pre-submission');
    }
    
    // Send the transaction
    const tx = await wallet.sendTransaction(txParams);
    console.log("Transaction sent:", tx.hash);
    
    // Add the transaction to the context with 'pending' status
    addTransaction({
      hash: tx.hash,
      type: TRANSACTION_TYPES.SWAP,
      from: walletAddress,
      to: txParams.to,
      value: amountIn,
      tokenSymbol: inputToken.symbol,
      tokenDecimal: inputDecimals,
      status: 'pending',
      network: contextChain?.key || 'unknown',
      data: JSON.stringify({
        inputToken: {
          symbol: inputToken.symbol,
          address: inputToken.address
        },
        outputToken: {
          symbol: outputToken.symbol,
          address: outputToken.address
        },
        amountOut: amountOutMin,
        protocol: 'Exchange'
      }),
      explorerUrl: explorerUrl ? `${explorerUrl}/tx/${tx.hash}` : ''
    });
    
    // Show processing notification if auto-confirm is enabled
    if (autoConfirm && notificationRef.current) {
      notificationRef.current.showProcessing(explorerUrl || '');
    }
    
    // Wait for confirmation
    const receipt = await tx.wait();
    console.log("Transaction confirmed:", receipt);
    
    // Update transaction status
    updateTransactionStatus(tx.hash, 'successSwap');
    
    // Update notification if auto-confirm is enabled
    if (autoConfirm && notificationRef.current) {
      notificationRef.current.showSuccess(receipt.hash, explorerUrl || '');
    }
    
    return {
      success: true,
      hash: receipt.hash,
      inputToken: inputToken,
      outputToken: outputToken,
      amount: amountIn,
      tokensInvolved: {
        input: {
          symbol: inputToken.symbol,
          address: inputToken.address
        },
        output: {
          symbol: outputToken.symbol,
          address: outputToken.address
        }
      }
    };
  } catch (error) {
    console.error("Execution error:", error);
    
    // Use the specialized error analysis utility
    if (typeof analyzeSushiSwapError === 'function') {
      analyzeSushiSwapError(error, routeSummary._tx, {
        inputToken: inputToken.symbol,
        outputToken: outputToken.symbol,
        amountIn,
        minimumOut: amountOutMin
      });
    } else if (typeof analyzeTransactionError === 'function') {
      analyzeTransactionError(error, {
        inputToken: inputToken.symbol,
        outputToken: outputToken.symbol,
        amountIn,
        minimumOut: amountOutMin
      });
    }
    
    // Update transaction status if we have a hash
    if (error.transaction && error.transaction.hash) {
      updateTransactionStatus(error.transaction.hash, 'failed');
    }
    
    // Hide notification if auto-confirm is enabled
    if (autoConfirm && notificationRef.current) {
      notificationRef.current.hide();
    }
    
    // Throw a user-friendly error message
    throw new Error(getReadableErrorMessage(error.message || t('swap.errors.unexpectedError')));
  }
};

  /**
 * Execute swap using direct contract call - implementation for BitDogSwap
 */
const executeContractSwap = async (
  inputToken,
  outputToken,
  amountIn,
  amountOutMin,
  slippage,
  gasOption,
  customGasPrice,
  dynamicGasPrice,
  userAddress,
  deadline,
  txOptions = {}
) => {
  // Get BitDogSwap contract with signer
  const aggregator = getBitDogSwapContract(provider, aggregatorAddress, currentWallet, true);
  if (!aggregator) throw new Error(t('swap.errors.aggregatorContractFailed'));
  
  // Get decimals for both tokens
  const inputDecimals = await getActualTokenDecimals(provider, inputToken);
  const outputDecimals = await getActualTokenDecimals(provider, outputToken);
  
  console.log("Token decimals for swap:", {
    inputToken: inputToken.symbol,
    inputDecimals,
    outputToken: outputToken.symbol,
    outputDecimals
  });
  
  // Calculate amountOutMin with slippage tolerance
  const amountInWei = parseUnits(amountIn.toString(), inputDecimals);
  
  // Set deadline based on user settings
  const deadlineMinutes = parseInt(deadline, 10) || 20; // Default to 20 minutes if invalid
  const deadlineSeconds = deadlineMinutes * 60;
  const deadlineTimestamp = Math.floor(Date.now() / 1000) + deadlineSeconds;
  
  // Prepare transaction options
  const options = { ...txOptions };
  
  // Add value for native token swaps
  if (isNativeToken(inputToken.address)) {
    console.log("Setting transaction value for native token:", amountInWei.toString());
    options.value = amountInWei;
  }
  
  // Apply gas settings based on user selection
  if (gasOption === 'custom' && customGasPrice) {
    try {
      // Format the gas price to ensure consistent precision
      const formattedGasPrice = formatGasPriceForTransaction(customGasPrice);
      // Parse gas price with proper units
      const parsedGasPrice = parseUnits(formattedGasPrice, 'gwei');
      txOptions.gasPrice = parsedGasPrice;
      console.log(`Using custom gas price from parameters: ${formattedGasPrice} Gwei`);
    } catch (error) {
      console.error("Error parsing custom gas price:", error);
    }
  } else if (dynamicGasPrice) {
    try {
      const basePrice = parseFloat(dynamicGasPrice);
      let multiplier = 1.0; // default medium
      
      if (gasOption === 'slow') multiplier = 0.8;
      else if (gasOption === 'fast') multiplier = 1.2;
      
      const calculatedPrice = (basePrice * multiplier);
      // Format the calculated price to ensure consistent precision
      const formattedGasPrice = formatGasPriceForTransaction(calculatedPrice);
      txOptions.gasPrice = parseUnits(formattedGasPrice, 'gwei');
      console.log(`Using ${gasOption} gas price: ${formattedGasPrice} Gwei (base: ${basePrice}, multiplier: ${multiplier})`);
    } catch (error) {
      console.error("Error setting dynamic gas price:", error);
    }
  }
  
  console.log("Executing swap:", {
    inputToken: inputToken.symbol,
    inputAddress: inputToken.address,
    outputToken: outputToken.symbol,
    outputAddress: outputToken.address,
    amountInWei: amountInWei.toString(),
    amountOutMin,
    deadline: deadlineTimestamp,
    deadlineInMinutes: deadlineMinutes,
    gasSettings: `${gasOption} (${customGasPrice || 'dynamic'} Gwei)`,
    options: JSON.stringify({
      value: options.value ? options.value.toString() : '0',
      gasPrice: options.gasPrice ? formatUnits(options.gasPrice, 'gwei') + ' Gwei' : 'default'
    })
  });
  
  // FIX: Convert amountOutMin to BigInt with proper decimals and handle very small values
  let minAmountOutWei;
  try {
    // Try to parse with proper decimals
    minAmountOutWei = parseUnits(amountOutMin.toString(), outputDecimals);
  } catch (error) {
    // Handle underflow errors for very small amounts
    if (error.code === 'NUMERIC_FAULT' && error.fault === 'underflow') {
      console.warn(`Value too small for token decimals: ${amountOutMin}, using minimum representable value`);
      // Use the smallest possible value for this token's decimal places
      minAmountOutWei = BigInt(1);
    } else {
      // Rethrow other errors
      throw error;
    }
  }
  
  // Execute swap with BitDogSwap aggregator
  const tx = await aggregator.swap(
    inputToken.address,
    outputToken.address,
    amountInWei,
    minAmountOutWei,
    deadlineTimestamp, // Use the calculated deadline timestamp
    options
  );
  
  console.log("Transaction submitted:", tx.hash);
  
  // Add the transaction to the context with 'pending' status
  addTransaction({
    hash: tx.hash,
    type: TRANSACTION_TYPES.SWAP,
    from: currentWallet.address,
    to: aggregatorAddress,
    value: amountIn,
    tokenSymbol: inputToken.symbol,
    tokenDecimal: inputDecimals,
    status: 'pending',
    // Use currentChain.key directly to match the filter in Transactions.js
    network: contextChain?.key || 'unknown',
    data: JSON.stringify({
      inputToken: {
        symbol: inputToken.symbol,
        address: inputToken.address
      },
      outputToken: {
        symbol: outputToken.symbol,
        address: outputToken.address
      },
      amountOut: amountOutMin
    }),
    explorerUrl: explorerUrl ? `${explorerUrl}/tx/${tx.hash}` : ''
  });
  
  // Show processing notification ONLY if auto-confirm is enabled
  if (autoConfirm && notificationRef.current) {
    notificationRef.current.showProcessing(explorerUrl || '');
  }
  
  // Wait for confirmation
  const receipt = await tx.wait();
  console.log("Transaction confirmed:", receipt);
  
  // Update transaction status in context to 'success'
  updateTransactionStatus(tx.hash, 'successSwap');
  
  // Update notification ONLY if auto-confirm is enabled
  if (autoConfirm && notificationRef.current) {
    notificationRef.current.showSuccess(receipt.hash, explorerUrl || '');
  }
  
  return {
    success: true,
    hash: receipt.hash,
    inputToken: inputToken,
    outputToken: outputToken,
    amount: amountIn,
    tokensInvolved: {
      input: {
        symbol: inputToken.symbol,
        address: inputToken.address
      },
      output: {
        symbol: outputToken.symbol,
        address: outputToken.address
      }
    }
  };
};

/**
 * Execute a two-step swap for wrapped native → ERC20
 * First unwraps the token, then swaps the native token to the desired ERC20
 */
const executeWrappedNativeToErc20Swap = async (
  inputToken,
  outputToken,
  amountIn,
  amountOutMin,
  slippage,
  gasOption,
  customGasPrice,
  dynamicGasPrice,
  userAddress,
  deadline
) => {
  setIsSwapping(true);
  
  try {
    // 1. First unwrap the token by directly calling the WETH contract
    console.log("Step 1: Unwrapping native token");
    
    // WETH ABI for unwrap function
    const WETH_ABI = [
      "function withdraw(uint wad) external"
    ];
    
    // Create wallet with signer
    const wallet = new ethers.Wallet(currentWallet.privateKey, provider);
    
    // Parse amount to wei
    const amountWei = parseUnits(amountIn.toString(), 18);
    
    // Get wrapped token contract
    const wrappedTokenContract = new ethers.Contract(
      currentChain.wrappedNativeToken,
      WETH_ABI,
      wallet
    );
    
    // Prepare transaction options
    const unwrapOptions = {};
    
    // Apply gas settings if provided
    if (gasOption === 'custom' && customGasPrice) {
      try {
        unwrapOptions.gasPrice = parseUnits(customGasPrice, 'gwei');
      } catch (error) {
        console.error("Error setting custom gas price for unwrap:", error);
      }
    } else if (dynamicGasPrice) {
      try {
        const basePrice = parseFloat(dynamicGasPrice);
        let multiplier = 1.0; // default medium
        
        if (gasOption === 'slow') multiplier = 0.8;
        else if (gasOption === 'fast') multiplier = 1.2;
        
        const calculatedPrice = (basePrice * multiplier).toFixed(9);
        unwrapOptions.gasPrice = parseUnits(calculatedPrice, 'gwei');
      } catch (error) {
        console.error("Error setting dynamic gas price for unwrap:", error);
      }
    }
    
    // Call the withdraw function to unwrap
    console.log(`Unwrapping ${amountIn} ${inputToken.symbol}`);
    const unwrapTx = await wrappedTokenContract.withdraw(amountWei, unwrapOptions);
    
    // Add unwrap transaction to history
    addTransaction({
      hash: unwrapTx.hash,
      type: TRANSACTION_TYPES.UNWRAP,
      from: wallet.address,
      to: currentChain.wrappedNativeToken,
      value: amountIn,
      tokenSymbol: inputToken.symbol,
      tokenDecimal: 18,
      status: 'pending',
      network: contextChain?.key || 'unknown',
      data: JSON.stringify({
        wrappedToken: currentChain.wrappedNativeToken,
        action: 'unwrap'
      }),
      explorerUrl: currentChain?.explorerUrl ? `${currentChain.explorerUrl}/tx/${unwrapTx.hash}` : ''
    });
    
    console.log("Unwrap transaction sent:", unwrapTx.hash);
    
    // Wait for the unwrap transaction to be confirmed
    console.log("Waiting for unwrap transaction confirmation...");
    const unwrapReceipt = await unwrapTx.wait();
    console.log("Unwrap confirmed:", unwrapReceipt.hash);
    
    // Update transaction status
    updateTransactionStatus(unwrapTx.hash, 'success');
    
    // Wait for a short time to ensure the balance updates
    console.log("Waiting for balance update...");
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // 2. Now swap native token to ERC20
    console.log("Step 2: Swapping native token to ERC20");
    
    // Create a native token object
    const nativeToken = {
      symbol: currentChain.nativeToken.symbol,
      name: currentChain.nativeToken.name,
      address: ETH_ADDRESS, // Special placeholder for native token
      decimals: 18,
      icon: currentChain.nativeToken.icon
    };
    
    // Execute the swap from native token to ERC20
    const swapResult = await executeContractSwap(
      nativeToken,
      outputToken,
      amountIn,  // Use the same amount
      amountOutMin,
      slippage,
      gasOption,
      customGasPrice,
      dynamicGasPrice,
      userAddress,
      deadline
    );
    
    return swapResult;
  } catch (error) {
    console.error("Error in wrapped native to ERC20 swap:", error);
    setSwapError("Failed to execute wrapped token swap: " + error.message);
    return { success: false, error: error.message };
  } finally {
    setIsSwapping(false);
  }
};

// Execute swap - main function exposed to Swap.js
const executeSwap = async (
  inputToken,
  outputToken,
  amountIn,
  amountOutMin,
  slippage,
  gasOption,
  customGasPrice,
  dynamicGasPrice,
  userAddress,
  deadline,
  routeSummary = null // For API calls
) => {
  // Validate input parameters
  if (!provider || !currentWallet || !userAddress) {
    setSwapError(t('swap.errors.missingWallet'));
    setIsConfirmationModalOpen(false); // Close modal on error
    return { success: false, error: t('swap.errors.missingWallet') };
  }
  
  if (!inputToken?.symbol || !outputToken?.symbol) {
    setSwapError(t('swap.errors.missingTokenSelection'));
    setIsConfirmationModalOpen(false); // Close modal on error
    return { success: false, error: t('swap.errors.missingTokenSelection') };
  }
  
  if (!currentWallet.privateKey) {
    setSwapError(t('swap.errors.walletConnectionIssue'));
    setIsConfirmationModalOpen(false); // Close modal on error
    return { success: false, error: t('swap.errors.walletConnectionIssue') };
  }
  
  // Validate amounts
  if (!isValidAmount(amountIn)) {
    setSwapError(t('swap.errors.invalidInputAmount'));
    setIsConfirmationModalOpen(false); // Close modal on error
    return { success: false, error: t('swap.errors.invalidInputAmount') };
  }
  
  if (!isValidAmount(amountOutMin)) {
    setSwapError(t('swap.errors.invalidMinimumOutputAmount'));
    setIsConfirmationModalOpen(false); // Close modal on error
    return { success: false, error: t('swap.errors.invalidMinimumOutputAmount') };
  }
  
  // Check if this is a wrapped native → ERC20 swap that needs special handling
  const isWrappedNativeToErc20 = currentChain?.wrappedNativeToken && 
    inputToken?.address?.toLowerCase() === currentChain.wrappedNativeToken.toLowerCase() &&
    outputToken?.address && 
    !isNativeToken(outputToken.address) &&
    outputToken.address.toLowerCase() !== currentChain.wrappedNativeToken.toLowerCase();

  if (isWrappedNativeToErc20 && !isUsingSushiSwap(currentChain)) {
    console.log("Using special handling for wrapped native → ERC20 swap");
    return executeWrappedNativeToErc20Swap(
      inputToken,
      outputToken,
      amountIn,
      amountOutMin,
      slippage,
      gasOption,
      customGasPrice,
      dynamicGasPrice,
      userAddress,
      deadline
    );
  }
  
  setIsSwapping(true);
  setSwapError(null);
  
  // Debug log the gas settings passed to this function
  console.log("executeSwap received gas settings:", {
    gasOption,
    customGasPrice,
    tempGasOption,
    tempGasPrice
  });
  
  try {
    // Prepare transaction options
    const txOptions = {};
    
    // CRITICAL FIX: Always use gas settings from parameters
    // We calculate the actual gas price based on the option
    console.log("Calculating gas price for option:", gasOption);
    
    if (gasOption === 'custom' && customGasPrice) {
      try {
        // Parse gas price with proper units
        const parsedGasPrice = parseUnits(customGasPrice, 'gwei');
        txOptions.gasPrice = parsedGasPrice;
        console.log(`Using custom gas price from parameters: ${customGasPrice} Gwei`);
      } catch (error) {
        console.error("Error parsing custom gas price:", error);
      }
    } else if (dynamicGasPrice) {
      try {
        const basePrice = parseFloat(dynamicGasPrice);
        let multiplier = 1.0; // default medium
        
        if (gasOption === 'slow') multiplier = 0.8;
        else if (gasOption === 'fast') multiplier = 1.2;
        
        const adjustedPrice = (basePrice * multiplier).toFixed(9);
        txOptions.gasPrice = parseUnits(adjustedPrice, 'gwei');
        console.log(`Using ${gasOption} gas price: ${adjustedPrice} Gwei (base: ${basePrice}, multiplier: ${multiplier})`);
      } catch (error) {
        console.error("Error setting dynamic gas price:", error);
      }
    }
    
    // CRITICAL: Get the wallet's actual address
    const wallet = new ethers.Wallet(currentWallet.privateKey, provider);
    const walletAddress = wallet.address;
    console.log("Using wallet address for swap:", walletAddress);
    
    // Check if we should use SushiSwap API or direct contract call
    let result;
    if (isUsingSushiSwap(currentChain)) {
      console.log("Executing swap using SushiSwap API");
      result = await executeSushiSwap(
        inputToken,
        outputToken,
        amountIn,
        amountOutMin,
        slippage,
        gasOption, // Pass the gas option parameter 
        customGasPrice,  // Pass the gas price parameter
        dynamicGasPrice,
        walletAddress, // Use wallet's address directly
        deadline,
        routeSummary, // Pass routeSummary for SushiSwap
        txOptions
      );
    } else {
      console.log("Executing swap using direct contract call");
      result = await executeContractSwap(
        inputToken,
        outputToken,
        amountIn,
        amountOutMin,
        slippage,
        gasOption, // Pass the gas option parameter
        customGasPrice,  // Pass the gas price parameter
        dynamicGasPrice,
        userAddress,
        deadline,
        txOptions
      );
    }
    
    // Update transaction hash state
    setTxnHash(result.hash);
    setIsSwapping(false);
    
    // Reset gas settings after successful swap
    setTempGasOption(null);
    setTempGasPrice(null);
    
    return result;
  } catch (err) {
    console.error("Swap failed:", err);
    
    // Extract error message and make it user-friendly
    const userFriendlyMessage = getReadableErrorMessage(err.message || t('swap.errors.unexpectedError'));
    setSwapError(`${t('swap.swapFailed')}: ${userFriendlyMessage}`);
    setIsSwapping(false);
    
    // IMPORTANT: Close the confirmation modal when an error occurs
    // This ensures the user can see the error message
    setIsConfirmationModalOpen(false);
    
    return {
      success: false,
      error: userFriendlyMessage
    };
  } finally {
    // Reset temporary gas settings
    setTempGasOption(null);
    setTempGasPrice(null);
  }
};
  
  // Prepare swap confirmation dialog details
  const prepareSwapConfirmation = async (
    inputToken,
    outputToken,
    amountIn,
    amountOut,
    inputTokenBalance,
    slippage,
    gasOption,
    customGasPrice,
    dynamicGasPrice,
    userAddress,
    bestRouter,
    bestDestRouter,
    priceImpact,
    aggregatorFeePercentage,
    collectFeesInWeth,
    nativeTokenSymbol,
    deadline,
    routeSummary
  ) => {
    if (!inputToken.symbol || !outputToken.symbol) {
      setSwapError(t('swap.errors.noTokenSelected'));
      return false;
    }
    
    if (!amountIn || parseFloat(amountIn) <= 0) {
      setSwapError(t('swap.errors.invalidAmount'));
      return false;
    }
    
    // Check balance
    const balance = parseFloat(inputTokenBalance);
    if (parseFloat(amountIn) > balance) {
      setSwapError(t('swap.insufficientBalance'));
      return false;
    }
    
    try {
      // Reset transaction hash for new transaction attempt
      setTxnHash(null);
      
      // Store gas settings from parameters (not temp state)
      setTempGasOption(gasOption);
      setTempGasPrice(customGasPrice);
      
      // Calculate gas cost
      const gasLimit = isUsingSushiSwap(currentChain) && routeSummary?._tx?.gas
        ? parseInt(routeSummary._tx.gas)
        : 500000; // Default high limit for aggregator swaps
      
      // Determine effective gas price based on settings
      let effectiveGasPrice;
      if (dynamicGasPrice) {
        const baseGasPrice = parseFloat(dynamicGasPrice);
        let multiplier;
        
        switch (gasOption) {
          case 'slow': multiplier = 0.8; break;
          case 'fast': multiplier = 1.2; break;
          case 'custom': 
            // Format the custom gas price to ensure consistent precision
            effectiveGasPrice = parseUnits(formatGasPriceForTransaction(customGasPrice || '3'), 'gwei');
            break;
          default: multiplier = 1.0; // medium
        }
        
        if (!effectiveGasPrice) {
          // Format the calculated price to ensure consistent precision
          const calculatedPrice = (baseGasPrice * multiplier);
          effectiveGasPrice = parseUnits(formatGasPriceForTransaction(calculatedPrice), 'gwei');
        }
      } else {
        // Fallback if dynamic gas price isn't available
        effectiveGasPrice = parseUnits(formatGasPriceForTransaction(customGasPrice || '3'), 'gwei');
      }
      
      // Calculate total gas cost - ensure proper BigInt handling
      const totalGasCost = effectiveGasPrice * BigInt(gasLimit);
      const gasCostEth = formatUnits(totalGasCost);
      
      // Format amounts for display in confirmation modal
      const formattedAmountIn = formatAmountForDisplay(amountIn);
      const formattedAmountOut = formatAmountForDisplay(amountOut);
      const formattedGasCost = formatAmountForDisplay(gasCostEth, 8);
      
      // Determine which swap method is being used
      const swapMethod = isUsingSushiSwap(currentChain) ? 'Exchange' : 'Exchange';
      
      // Prepare routing info based on swap method
      let routingInfo;
      if (isUsingSushiSwap(currentChain)) {
        routingInfo = `${t('swap.approval.router', { name: 'Exchange Router' })}`;
      } else {
        routingInfo = bestDestRouter ? 
          `${t('swap.routeInfo')}: ${bestRouter ? t('swap.bestLiquidityPath') : t('swap.unknown')} → ${bestDestRouter ? t('swap.bestDestination') : t('swap.unknown')}` : 
          `${t('swap.approval.router', { name: bestRouter ? t('swap.bestLiquidityPath') : t('swap.unknown') })}`;
      }
    
      // Ensure we're using the wallet's actual address for accuracy
      const wallet = new ethers.Wallet(currentWallet.privateKey, provider);
      const walletAddress = wallet.address;
    
      // Create transaction details for confirmation modal
      const details = {
        type: t('swap.title'),
        from: walletAddress, // Use wallet's address directly
        to: isUsingSushiSwap(currentChain) ? 
          (routeSummary?._tx?.to || currentChain.sushiswapConfig?.routerAddress) : 
          aggregatorAddress,
        spendingAmount: `${formattedAmountIn} ${inputToken.symbol}`,
        receivingAmount: `${formattedAmountOut} ${outputToken.symbol}`,
        slippage: `${slippage}%`,
        priceImpact: priceImpact ? `${priceImpact.toFixed(2)}%` : t('common.unknown'),
        deadline: `${deadline} ${t('swap.settings.minutes')}`,
        swapFee: `${aggregatorFeePercentage.toFixed(2)}%${collectFeesInWeth ? ` ${t('swap.settings.inWETH')}` : ''}`,
        routingInfo: routingInfo,
        swapMethod: swapMethod,
        gasFee: `${formattedGasCost} ${nativeTokenSymbol}`,
      };
    
      setConfirmationDetails(details);
      return true;
    } catch (err) {
      console.error("Error preparing swap:", err);
      setSwapError(t('swap.errors.preparationFailed', { message: getReadableErrorMessage(err.message) }));
      return false;
    }
  };

  const handleGasSettingChange = (option, price) => {
    console.log("Gas settings changed:", option, price);
    setTempGasOption(option);
    setTempGasPrice(price);
  };

  return {
    isSwapping,
    txnHash,
    swapError,
    tempGasOption,
    tempGasPrice,
    confirmationDetails,
    isConfirmationModalOpen,
    setSwapError,
    setIsConfirmationModalOpen,
    setTxnHash,
    executeSwap,
    prepareSwapConfirmation: async (...args) => {
      // Wrap the existing function with error handling
      try {
        return await prepareSwapConfirmation(...args);
      } catch (err) {
        console.error("Error preparing swap:", err);
        setSwapError(t('swap.errors.preparationFailed', { message: getReadableErrorMessage(err.message) }));
        return false;
      }
    },
    handleGasSettingChange
  };
};

export default useSwapExecution;